


















import Enumerable from "linq";
import { Component, Mixins, Watch } from "vue-property-decorator";
import { Directory } from "../../../../../../domain/drawing";
import { IClaioImageSource } from "../../../../../../domain/claio";
import { TabContainableMixin } from "../../../../../system/mixins";
import TabSelector from "../../../../../system/selectors/TabSelector.vue";
import CacheableComponent from "../../../../../system/CacheableComponent.vue";
import { ClaioImageRootReference } from "../ClaioImageRootReference";
import { ClaioImageReference } from "../ClaioImageReference";
import {
  ClaioContentsTreeSelector as DateTree,
  ClaioContentsTreeSelector as KindTree,
} from "../claio";
import { ClaioContentsFilterMixin } from "./mixins";

interface IClaioImageTreeTab {
  name: string;
  description: string;
  title: string;
  toPrimaryGroupLabel: (c: IClaioImageSource) => string;
  toSecondaryGroupLabel: (c: IClaioImageSource) => string;
  toGroupKey: (c: IClaioImageSource) => string;
  toEntities: (c: IClaioImageSource[]) => IClaioImageSource[];
}

@Component({
  components: {
    CacheableComponent,
    DateTree,
    KindTree,
    TabSelector,
  }
})
export default class ClaioContentsTreeFilter extends Mixins(ClaioContentsFilterMixin, TabContainableMixin) {
  readonly Tabs: {[key: string]: IClaioImageTreeTab} = {
    date: {
      name: "date",
      description: "日付毎に表示します。",
      title: "日付順",
      toPrimaryGroupLabel: (c) => this.toDateGroupLabel(c),
      toSecondaryGroupLabel: (c) => this.toImageKindGroupLabel(c),
      toGroupKey: (c) => this.toDateGroupKey(c),
      toEntities: (c) => this.distinctImageKind(c),
    },
    kind: {
      name: "kind",
      description: "種類毎に表示します。",
      title: "種別順",
      toPrimaryGroupLabel: (c) => this.toImageKindGroupLabel(c),
      toSecondaryGroupLabel: (c) => this.toDateGroupLabel(c),
      toGroupKey: (c) => this.toKindGroupKey(c),
      toEntities: (c) => this.distinctImageDateAndDepartmentId(c),
    },
  };

  $refs!: {
    dateTree: DateTree,
    kindTree: KindTree,
  };

  data(): any {
    return {
      dateTreeRoot: null,
      kindTreeRoot: null,
    };
  }
  dateTreeRoot: ClaioImageRootReference;
  kindTreeRoot: ClaioImageRootReference;

  created(): void {
    this.initializeTabs();
    this.selectedTab = this.Tabs.date;
  }

  initializeTabs(): void {
    this.tabs = [
      this.Tabs.date,
      this.Tabs.kind,
    ];
  }

  get isDateTabSelected(): boolean {
    return this.selectedTab.name === this.Tabs.date.name;
  }

  private toDateGroupLabel(source: IClaioImageSource): string {
    return this.getImageSourceOptionName(source);
  }

  private toImageKindGroupLabel(source: IClaioImageSource): string {
    return source.imageKind
      ? source.imageKind
      : "種別なし";
  }

  private toDateGroupKey(source: IClaioImageSource): string {
    return JSON.stringify({ imageDate: source.imageDate.toString(), departmentId: source.departmentId });
  }

  private toKindGroupKey(source: IClaioImageSource): string {
    return source.imageKind;
  }

  private distinctImageKind(source: IClaioImageSource[]): IClaioImageSource[] {
    return Enumerable.from(source).distinct(g => g.imageKind).toArray();
  }

  private distinctImageDateAndDepartmentId(source: IClaioImageSource[]): IClaioImageSource[] {
    return Enumerable.from(source).distinct(g => g.imageDate.toDateString() + g.departmentId).toArray();
  }

  private updateRoot(source: IClaioImageSource[]): void {
    this.dateTreeRoot = new ClaioImageRootReference(this.toClaioImageReferenceDirectories(this.Tabs.date, source));
    this.kindTreeRoot = new ClaioImageRootReference(this.toClaioImageReferenceDirectories(this.Tabs.kind, source));
  }

  private toClaioImageReferenceDirectories(tab: IClaioImageTreeTab, source: IClaioImageSource[]): Directory<number, ClaioImageReference>[] {
    return Enumerable.from(source)
      .groupBy(c => tab.toGroupKey(c))
      .select((s, i) => this.toClaioImageReferenceDirectory(i, tab, tab.toEntities(s.getSource())))
      .toArray();
  }

  private toClaioImageReferenceDirectory(index: number, tab: IClaioImageTreeTab, images: IClaioImageSource[]): Directory<number, ClaioImageReference> {
    return new Directory(
      index,
      tab.toPrimaryGroupLabel(images[0]),
      null,
      this.toClaioImageReferences(tab, images)
    );
  }

  private toClaioImageReferences(tab: IClaioImageTreeTab, images: IClaioImageSource[]): ClaioImageReference[] {
    return Enumerable.from(images)
      .select(i => this.toClaioImageReference(tab, i))
      .toArray();
  }

  private toClaioImageReference(tab: IClaioImageTreeTab, image: IClaioImageSource): ClaioImageReference {
    return new ClaioImageReference(
      tab.toSecondaryGroupLabel(image),
      image.imageDate,
      image.imageKind,
      image.departmentId,
      (s, d) => this.areEqual(s, d)
    );
  }

  private containsDirectory(source: IClaioImageSource, destination: ClaioImageReference): boolean {
    return this.isDateTabSelected
      ? this.containsDateTabDirectory(source, destination)
      : this.containsKindTabDirectory(source, destination);
  }

  private containsDateTabDirectory(source: IClaioImageSource, destination: ClaioImageReference): boolean {
    return (source.imageDate.toDateString() === destination.imageDate.toDateString())
      && (source.departmentId === destination.departmentId);
  }

  private containsKindTabDirectory(source: IClaioImageSource, destination: ClaioImageReference): boolean {
    return (source.imageKind === destination.imageKind);
  }

  private areEqual(source: IClaioImageSource, destination: ClaioImageReference): boolean {
    return source.imageDate.toDateString() === destination.imageDate.toDateString()
      && source.imageKind === destination.imageKind
      && source.departmentId === destination.departmentId;
  }

  private extractSource(reference: ClaioImageReference): void {
    this.editingSource = (reference !== null)
      ? Enumerable.from(this.source)
          .where(s => reference.areEqual ? reference.areEqual(s, reference) : this.containsDirectory(s, reference))
          .toArray()
      : [];
  }

  @Watch("selectedSource")
  onSelectedSourceChanged(newValue: ClaioImageReference) {
    this.extractSource(newValue);
  }

  @Watch("source")
  onSourceUpdated(newValue: IClaioImageSource[]) {
    // ツリーは設定にかかわらず降順。（サムネイルは設定に従う）
    const descendingSource = this.shouldOrderByAscending
      ? newValue.concat().reverse()
      : newValue;
    this.updateRoot(descendingSource);
  }

  @Watch("selectedTab")
  onSelectedTabChanged(oldValue: { name: string, description: string }) {
    if (oldValue) {
      // タブを切り替えたらツリーの選択とサムネイルの表示をクリアする
      this.extractSource(null);
      if (oldValue.name === this.Tabs.date.name && this.$refs.dateTree) {
        this.$refs.dateTree.clearSelection();
      }
      if (oldValue.name === this.Tabs.kind.name && this.$refs.kindTree) {
        this.$refs.kindTree.clearSelection();
      }
    }
  }
}
